import React, { useEffect, useState } from 'react';
import { CardHolder, MenuBtn, StyledAgentProfile } from './AgentProfile.style';
import { useParams } from 'react-router-dom';
import agentService from '../../services/agentService';
import { useQuery } from '@tanstack/react-query';
import cardIcon from '../../assets/feeda-icon.svg';
import { Brain, Clock, Settings2 } from 'lucide-react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import memoryService from '../../services/memoryService';
import { formatDate } from '../../helpers/common';
import { ReponseMenu, StyleItem } from '../SideBar/SideBar.styles';
import toolsService from '../../services/toolsService';
import Loaders from '../Loaders';

const AgentProfile = () => {
  const { id } = useParams();
  const [tools, setTools] = useState([]);

  const get_single_agent = useQuery({
    queryKey: ['get-single-agent'],
    queryFn: () => agentService.getAgentDetail(id),
    enabled: !!id,
  });

  const { fetch } = useContextHook(AuthContext, v => ({
    fetch: v.fetch,
  }));

  const { _data } = memoryService.GetMemoryList(id, fetch);

  const getTools = async () => {
    try {
      const resp = await toolsService.getAllTools('user_created');
      const resp2 = await toolsService.getAllTools('admin_created');

      setTools([...resp?.tools, ...resp2?.tools]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getTools();
  }, [get_single_agent.isSuccess, id]);

  return (
    <>
      {get_single_agent.isFetching ? (
        <Loaders pageLoader />
      ) : (
        <StyledAgentProfile>
          <div className="head">
            <div className="img-box">
              <img src={get_single_agent?.data?.detail?.icon ?? cardIcon} alt="img" />
            </div>
            <div className="head-details">
              <strong className="name">{get_single_agent?.data?.detail?.agent_name}</strong>
              <span className="description">{get_single_agent?.data?.detail?.description}</span>
              <span className="time">
                <Clock size={18} />
                <span>Updated 2024-11-27</span>•<span>Created 2024-01-15</span>
              </span>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <CardHolder>
                <div className="card-header">
                  <strong className="card-title">
                    <Settings2 size={20} />
                    Instructions
                  </strong>
                </div>
                <div className="card-details">
                  <p
                    className="instruction"
                    dangerouslySetInnerHTML={{
                      __html: get_single_agent?.data?.detail?.instructions?.replace(/\r\n/g, '<br>'),
                    }}></p>
                </div>
              </CardHolder>

              {get_single_agent?.data?.detail?.enable_user_memories && _data?.results?.results?.length > 0 && (
                <CardHolder>
                  <div className="card-header">
                    <strong className="card-title">
                      <Brain size={20} />
                      Memory
                    </strong>
                    <div className="total-memories">{_data?.results?.results?.length} items</div>
                  </div>
                  <div className="card-details">
                    {_data?.results?.results?.slice(0, 3)?.map((data, index) => (
                      <div className="memory" key={index}>
                        <div>
                          <strong className="memory-heading">{data?.memory}</strong>
                          <strong className="memory-date">{formatDate(data?.created_at)}</strong>
                        </div>
                        <div className="icon-wrap">
                          {data?.categories && (
                            <div className="category-wrap">
                              <div className="category">{data?.categories[0]}</div>
                              {data?.categories?.length > 1 && (
                                <ReponseMenu
                                  menuButton={
                                    <MenuBtn>
                                      <div className="category">+ {data?.categories?.length - 1} more</div>
                                    </MenuBtn>
                                  }>
                                  {data?.categories?.slice(1)?.map((item, index) => (
                                    <StyleItem key={index}>
                                      <div className="text">{item}</div>
                                    </StyleItem>
                                  ))}
                                </ReponseMenu>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardHolder>
              )}
            </div>
            <div className="col-2">
              <CardHolder $noBodyPadding>
                <div className="card-header">
                  <strong className="card-title">Configuration</strong>
                </div>
                <div className="card-details">
                  <ul className="list-wrap">
                    <li>
                      <span className="li-title">Provider</span>
                      <strong className="li-value">
                        {get_single_agent?.data?.detail?.model_id?.includes('gpt')
                          ? 'OpenAI'
                          : get_single_agent?.data?.detail?.model_id.split('/')[0]}
                      </strong>
                    </li>
                    <li>
                      <span className="li-title">Response Model</span>
                      <strong className="li-value">
                        {get_single_agent?.data?.detail?.model_id?.includes('gpt')
                          ? get_single_agent?.data?.detail?.model_id
                          : get_single_agent?.data?.detail?.model_id.split('/')[1]}
                      </strong>
                    </li>
                    <li>
                      <span className="li-title">Memory</span>
                      <strong
                        className={`li-value  ${
                          get_single_agent?.data?.detail?.enable_user_memories ? 'enabled' : 'disabled'
                        }`}>
                        {get_single_agent?.data?.detail?.enable_user_memories ? 'Enabled' : 'Disabled'}
                      </strong>
                    </li>
                  </ul>
                </div>
              </CardHolder>
              {get_single_agent?.data?.detail?.tools?.length > 0 && (
                <CardHolder>
                  <div className="card-header">
                    <strong className="card-title">Tools & Integrations</strong>
                  </div>
                  <div className="card-details">
                    {tools
                      ?.filter(tool => get_single_agent?.data?.detail?.tools?.includes(tool?.tool_id))
                      ?.map((data, index) => (
                        <div className="tool-name" key={index}>
                          {data?.tool_name}
                        </div>
                      ))}
                  </div>
                </CardHolder>
              )}
            </div>
          </div>
        </StyledAgentProfile>
      )}
    </>
  );
};

export default AgentProfile;
